<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import DealCard from './DealCard.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  deals: {
    type: Array,
    default: () => [],
  },
  isMoving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['moveStage']);

const { t } = useI18n();

const localDeals = computed({
  get: () => props.deals,
  set: () => {
    // The parent handles the actual data mutation via the move-stage event.
    // vuedraggable requires a setter but the real update happens on end.
  },
});

const dealsCount = computed(() => props.deals.length);

const probabilityLabel = computed(() => {
  if (props.stage.probability == null) return '';
  return `${props.stage.probability}%`;
});

const handleChange = event => {
  if (event.added) {
    emit('moveStage', event.added.element.id, props.stage.id);
  }
};
</script>

<template>
  <div
    class="flex flex-col w-72 flex-shrink-0 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 max-h-full"
  >
    <div
      class="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span
          class="text-sm font-medium text-slate-700 dark:text-slate-200 truncate"
        >
          {{ stage.name }}
        </span>
        <span class="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">
          ({{ dealsCount }})
        </span>
      </div>
      <span
        v-if="probabilityLabel"
        class="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0"
      >
        {{ probabilityLabel }}
      </span>
    </div>

    <div
      class="flex-1 overflow-y-auto p-2"
      :class="{ 'opacity-60 pointer-events-none': isMoving }"
    >
      <draggable
        v-model="localDeals"
        :group="{ name: 'deals', pull: true, put: true }"
        item-key="id"
        :animation="150"
        ghost-class="opacity-30"
        class="flex flex-col gap-2 min-h-4"
        @change="handleChange"
      >
        <template #item="{ element }">
          <DealCard :deal="element" />
        </template>
      </draggable>

      <div
        v-if="dealsCount === 0"
        class="flex items-center justify-center py-6 text-xs text-slate-400 dark:text-slate-600"
      >
        {{ t('DEALS.KANBAN.EMPTY_COLUMN') }}
      </div>
    </div>
  </div>
</template>
